import { supabase } from '@/integrations/supabase/client';
import {
  approvalValueFor,
  RULE_PARAM,
  ruleContainsFilter,
  WORKBENCH_STATUSES,
  type WorkbenchRuleId,
  type WorkbenchStatus,
} from '@/hooks/workbench/statusScope';

/**
 * Vom Befund im Feed-Check zu genau den betroffenen Produkten in der Werkbank
 * (UX-Audit WP5, A2O-02). Vorher fuehrte "In der Werkbank pruefen" auf
 * `/workbench` ohne Parameter; auf Spielgigant liegen alle 3.708 Produkte im
 * Reiter "Freigegeben", die Werkbank oeffnete "Offen 0" und schickte mit ihrem
 * Leerzustand zurueck zum Feed-Check.
 */
export type WorkbenchReadinessKey = 'brandPrefix' | 'titleLength' | 'normClaims';

export const READINESS_RULE_ID: Record<WorkbenchReadinessKey, WorkbenchRuleId> = {
  brandPrefix: 'brand_prefix_mismatch',
  titleLength: 'title_too_long',
  normClaims: 'norm_claim_ungrounded',
};

export type RuleStatusCounts = Record<WorkbenchStatus, number>;

/**
 * Der Reiter mit den meisten Treffern. Bei Gleichstand gewinnt die Reihenfolge
 * von `WORKBENCH_STATUSES` (offen vor freigegeben vor abgelehnt): liegt die
 * Arbeit gleich verteilt, ist die offene die dringendere. Ueberall 0 heisst
 * "Offen"; die Werkbank zeigt dann ihren Filter-Leerzustand mit Ausweg.
 */
export function pickRuleTab(counts: RuleStatusCounts): WorkbenchStatus {
  let best: WorkbenchStatus = WORKBENCH_STATUSES[0];
  for (const status of WORKBENCH_STATUSES) {
    if (counts[status] > counts[best]) best = status;
  }
  return best;
}

export function ruleWorkbenchTarget(ruleId: WorkbenchRuleId, status: WorkbenchStatus | null): string {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  params.set(RULE_PARAM, ruleId);
  return `/workbench?${params.toString()}`;
}

/**
 * Drei Kopfzaehlungen parallel, dasselbe Praedikat wie die Werkbank selbst
 * (`useWorkbench.ts`: `optimization_status = 'completed'`, Reiter, Regel), damit
 * die gewaehlte Menge die ist, die die Werkbank danach laedt.
 *
 * Das jsonb-Praedikat kommt ueber `ruleContainsFilter` (Ruling D13, Task 3
 * `statusScope.ts`): eine zweite Stelle, die `[{ rule_id }]` selbst baut, ist
 * genau die Kopie, die auseinanderlaeuft, sobald die Werkbank ihr Praedikat
 * einmal aendert.
 */
export async function fetchRuleStatusCounts(feedId: string, ruleId: WorkbenchRuleId): Promise<RuleStatusCounts> {
  const entries = await Promise.all(
    WORKBENCH_STATUSES.map(async (status) => {
      const { count, error } = await supabase
        .from('products')
        .select('id', { count: 'exact', head: true })
        .eq('feed_id', feedId)
        .eq('optimization_status', 'completed')
        .eq('approval_status', approvalValueFor(status))
        .contains('validation_warnings', JSON.stringify(ruleContainsFilter(ruleId)));
      if (error) throw error;
      return [status, count ?? 0] as const;
    }),
  );
  return Object.fromEntries(entries) as RuleStatusCounts;
}
